import { Component, ElementRef, Injector, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { takeUntil } from 'rxjs/operators';
import * as toastr from 'toastr';
import { EstoqueService } from '../../almoxarifado/estoque/service/estoque.service';
import { SetorService } from '../../almoxarifado/setor/service/setor.service';
import { Sistemas } from '../../components/types';
import { Estoque } from '../../entidade/almoxarifado/estoque.model';
import { Setor } from '../../entidade/almoxarifado/setor.model';
import { UsuarioEstoque } from '../../entidade/almoxarifado/usuario-estoque.model';
import { UsuarioSetor } from '../../entidade/almoxarifado/usuario-setor.model';
import { Favorecido } from '../../entidade/compra/favorecido.model';
import { Acesso } from '../../entidade/comum/acesso.model';
import {
  AlmoxarifadoPag, CompraPag, ComumPag, ContabilidadePag, ControleInternoPag, DiarioOficialPag, FolhaPag, FrotaPag, LegislativoPag,
  Pagina, PatrimonioPag, PlanejamentoPag, PortalEntidadePag, ProtocoloPag, TerceiroSetorPag, TesourariaPag, TransparenciaPag
} from '../../entidade/comum/pagina';
import { Usuario } from '../../entidade/comum/usuario.model';
import { LoginContabil } from '../../entidade/login/login-contabil';
import { Unidade } from '../../entidade/planejamento/unidade.model';
import { FavorecidoService } from '../../favorecido/service/favorecido.service';
import { BaseResourceFormComponent } from '../../models/base-resource-form';
import { EddyAutoComplete } from '../../models/form-components';
import { UnidadeService } from '../../unidade/service/unidade.service';
import { FuncaoService } from '../../util/funcao.service';
import { GlobalService } from '../../util/global.service';
import { UsuarioService } from '../service/usuario.service';

@Component({
  selector: 'lib-usuario-form',
  templateUrl: './usuario-form.component.html'
})
export class UsuarioFormComponent extends BaseResourceFormComponent<Usuario, LoginContabil> {

  /**
   * Declaração de variáveis
   */
  @ViewChild('tipo_') inputField: ElementRef;

  public setorAutoComplete: EddyAutoComplete<Setor>;
  public estoqueAutoComplete: EddyAutoComplete<Estoque>;
  public unidadeAutoComplete: EddyAutoComplete<Unidade>;
  public favorecidoAutoComplete: EddyAutoComplete<Favorecido>;
  public acessos: { sistema: Sistemas, acessos: Acesso[] }[];
  public opcoesAcesso: any[] = [
    { label: 'Sem acesso', value: 0, icon: 'lock_break.png' },
    { label: 'Visualizar', value: 1, icon: 'zoom.png' },
    { label: 'Incluir/Alterar', value: 2, icon: 'pencil.png' }
  ];
  public listaSistemas: { id: Sistemas, nome: string }[];
  public listaUsuariosCC: Usuario[];
  public acessosCC: Acesso[];

  public estoqueAtual: Estoque;
  public setorAtual: Setor;

  /**
   * Construtor com as injeções de dependencias
   */
  constructor(
    protected injector: Injector,
    protected messageService: MessageService,
    public globalService: GlobalService,
    protected funcaoService: FuncaoService,
    protected setorService: SetorService,
    protected estoqueService: EstoqueService,
    protected unidadeService: UnidadeService,
    protected favorecidoService: FavorecidoService,
    protected usuarioService: UsuarioService) {
    super(new Usuario(), injector, Usuario.converteJson, usuarioService);
  }

  protected criarCamposForm(): void {
    this.entidadeForm = this.fb.group({
      id: [null],
      nome: [null, [Validators.required, Validators.minLength(2)]],
      sobrenome: [null, [Validators.required, Validators.minLength(2)]],
      email: [null, [Validators.required, Validators.minLength(2)]],
      senha: [null],
      sistema: [null],
      cpf: [null, [Validators.required]],
      telefone: [null, [Validators.required]],
      ativo: [null],
      convidado: [null, [Validators.required]],
      administrador: [null, [Validators.required]],
      sabado: [true],
      domingo: [true],
      solicitacao: [null],
      setor: [null],
      estoque: [null],
      unidade: [null],
      favorecido: [null],
      horario_inicio: [null],
      horario_termino: [null],
      orgao: [null, [Validators.required]],
      acessos: [null],
      usuario_estoques: [null],
      usuario_setores: [null],
    });
  }

  protected parametrosExtras(): {} {
    return { relations: 'orgao,acessos,setor,estoque,favorecido,unidade,usuario_estoques.estoque,usuario_setores.setor' };
  }

  protected afterLoad() {
    this.preencherAcessos(this.entidade.acessos);
  }

  protected afterInit(): void {
    this.listaSistemas = this.globalService.obterListaSistemas();
    this.carregarAutoCompletes();
  }

  protected campoFoco(): ElementRef {
    return this.inputField;
  }

  protected beforeSubmit(): void {
    this.entidadeForm.get('acessos').setValue(this.unificarPermissoes());
    this.entidadeForm.get('usuario_estoques').setValue(this.entidade.usuario_estoques);
    this.entidadeForm.get('usuario_setores').setValue(this.entidade.usuario_setores);
  }

  protected afterSubmit(entidade: Usuario): void {
    this.mensagemSucesso = `Registro salvo com sucesso!`;
    if (this.currentActionRoute === 'novo' && this.login.redirecionar_liquidar_pagar
      && this.podeIncluir('/liquidacoes')) {
      this.router.navigate(['/liquidacoes', 'novo', entidade.id]);
    } else if (this.currentActionRoute === 'novo' && !this.limparTela) {
      this.router.navigate(['/empenhos', entidade.id, 'editar']);
    }
  }

  // ========================================================================
  //                            MÉTODOS DA CLASSE
  // ========================================================================

  public verificaCheck() {
    if (this.entidadeForm.get('administrador').value) {
      this.entidadeForm.get('convidado').setValue(false);
    }
    if (this.entidadeForm.get('convidado').value) {
      this.entidadeForm.get('administrador').setValue(false);
    }
  }

  private carregarAutoCompletes() {
    // autocomplete para setor
    this.setorAutoComplete = new EddyAutoComplete(this.entidadeForm.get('setor'), this.setorService,
      'id', ['codigo', 'nome'],
      { orgao_id: this.login.orgao.id, relations: 'unidade,orgao', orderBy: 'nome' }, { number: ['codigo'], text: ['nome'] });

    // autocomplete para estoque
    this.estoqueAutoComplete = new EddyAutoComplete(this.entidadeForm.get('estoque'), this.estoqueService,
      'id', ['nome'],
      { orgao_id: this.login.orgao.id, orderBy: 'nome' }, { text: ['nome'] });

    // autocomplete para favorecido
    this.favorecidoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('favorecido'), this.favorecidoService,
      'id', ['nome'], { cidade_id: this.login.cidade.id, orderBy: 'nome' }, { number: ['id', 'cpf_cnpj'], text: ['nome'] });

    // autocomplete para unidade administrativa 3 setor
    this.unidadeAutoComplete = new EddyAutoComplete(this.entidadeForm.get('unidade'), this.unidadeService,
      'id', ['codigo', 'nome'], { ppa_id: this.login.ppa.id, orderBy: 'nome' }, { number: ['codigo'], text: ['nome'] });
  }

  autorizar() {
    this.entidade.solicitacao = false;
    this.entidade.ativo = true;
    this.usuarioService.autorizar(this.entidade).pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.entidadeForm.get('solicitacao').setValue(this.entidade.solicitacao);
        this.entidadeForm.get('ativo').setValue(this.entidade.ativo);
        this.submitForm();
      });
  }

  private unificarPermissoes(): Acesso[] {
    const retorno: Acesso[] = [];

    // unifica os acessos dos sistemas em um só para ser salvo
    for (const acesso of this.acessos) {
      for (const acessoPagina of acesso.acessos) {
        retorno.push(acessoPagina);
      }
    }

    return retorno;
  }

  public carregarUsuariosCC() {
    this.usuarioService.filtrar(1, -1, {
      'orgao.id': this.login.orgao.id,
      id$ne: this.entidade.id,
      convidado: false,
      relations: 'acessos',
      orderBy: 'nome'
    }).pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        this.listaUsuariosCC = res ? res.content : [];
      }, error => toastr.error('Atenção', error));
  }

  public importarPermissoes() {
    if (!this.acessosCC || this.acessosCC.length === 0) {
      toastr.warning('Selecione o usuário a ter as suas permissões importadas!');
      return;
    }

    const acessos: Acesso[] = this.unificarPermissoes();

    for (const acessoCC of this.acessosCC) {
      for (const acesso of acessos) {
        if (acessoCC.pagina === acesso.pagina) {
          acesso.permissao = acessoCC.permissao;
        }
      }
    }

    this.preencherAcessos(acessos);

    toastr.info('Permissões importadas!');
  }

  public preencherAcessos(acessosEntidade: Acesso[]) {
    const acessosTotal: Acesso[] = [];

    // unifica todas as paginas em um objeto para percorrer do banco de dados e criar os que nao existirem
    let paginas = new ComumPag().paginas();
    paginas = paginas.concat(new AlmoxarifadoPag().paginas());
    paginas = paginas.concat(new CompraPag().paginas());
    paginas = paginas.concat(new ContabilidadePag().paginas());
    paginas = paginas.concat(new ControleInternoPag().paginas());
    paginas = paginas.concat(new DiarioOficialPag().paginas());
    paginas = paginas.concat(new FolhaPag().paginas());
    paginas = paginas.concat(new FrotaPag().paginas());
    paginas = paginas.concat(new LegislativoPag().paginas());
    paginas = paginas.concat(new PatrimonioPag().paginas());
    paginas = paginas.concat(new PlanejamentoPag().paginas());
    paginas = paginas.concat(new PortalEntidadePag().paginas());
    paginas = paginas.concat(new ProtocoloPag().paginas());
    paginas = paginas.concat(new TerceiroSetorPag().paginas());
    paginas = paginas.concat(new TesourariaPag().paginas());
    paginas = paginas.concat(new TransparenciaPag().paginas());

    for (const pagina of paginas) {
      let encontrou = false;
      if (acessosEntidade) {
        for (const acesso of acessosEntidade) {
          if (acesso.pagina === Pagina[pagina]) {
            acesso.usuario = this.login.usuario;
            acesso.nomePagina = pagina;
            acessosTotal.push(acesso);
            encontrou = true;
            break;
          }
        }
      }
      if (!encontrou) {
        const acesso = new Acesso();
        acesso.id = null;
        acesso.pagina = Pagina[pagina];
        acesso.permissao = 0;
        acesso.usuario = this.login.usuario;
        acesso.nomePagina = pagina;
        acessosTotal.push(acesso);
      }
    }

    // atribuir os acessos por sistema
    this.acessos = [];
    let acessosSistema: Acesso[];
    // Comum
    acessosSistema = [];
    for (const pagina of new ComumPag().paginas()) {
      for (const acesso of acessosTotal) {
        if (acesso.pagina === Pagina[pagina]) {
          acessosSistema.push(acesso);
          break;
        }
      }
    }
    this.acessos.push({ sistema: 'comum', acessos: acessosSistema });

    // Almoxarifado
    acessosSistema = [];
    for (const pagina of new AlmoxarifadoPag().paginas()) {
      for (const acesso of acessosTotal) {
        if (acesso.pagina === Pagina[pagina]) {
          acessosSistema.push(acesso);
          break;
        }
      }
    }
    this.acessos.push({ sistema: 'almoxarifado', acessos: acessosSistema });

    // Compra
    acessosSistema = [];
    for (const pagina of new CompraPag().paginas()) {
      for (const acesso of acessosTotal) {
        if (acesso.pagina === Pagina[pagina]) {
          acessosSistema.push(acesso);
          break;
        }
      }
    }
    this.acessos.push({ sistema: 'compras', acessos: acessosSistema });

    // Contabilidade
    acessosSistema = [];
    for (const pagina of new ContabilidadePag().paginas()) {
      for (const acesso of acessosTotal) {
        if (acesso.pagina === Pagina[pagina]) {
          acessosSistema.push(acesso);
          break;
        }
      }
    }
    this.acessos.push({ sistema: 'contabil', acessos: acessosSistema });

    // ControleInterno
    acessosSistema = [];
    for (const pagina of new ControleInternoPag().paginas()) {
      for (const acesso of acessosTotal) {
        if (acesso.pagina === Pagina[pagina]) {
          acessosSistema.push(acesso);
          break;
        }
      }
    }
    this.acessos.push({ sistema: 'controle-interno', acessos: acessosSistema });

    // DiarioOficial
    acessosSistema = [];
    for (const pagina of new DiarioOficialPag().paginas()) {
      for (const acesso of acessosTotal) {
        if (acesso.pagina === Pagina[pagina]) {
          acessosSistema.push(acesso);
          break;
        }
      }
    }
    this.acessos.push({ sistema: 'diario-oficial', acessos: acessosSistema });

    // Frota
    acessosSistema = [];
    for (const pagina of new FrotaPag().paginas()) {
      for (const acesso of acessosTotal) {
        if (acesso.pagina === Pagina[pagina]) {
          acessosSistema.push(acesso);
          break;
        }
      }
    }
    this.acessos.push({ sistema: 'frota', acessos: acessosSistema });

    // Legislativo
    acessosSistema = [];
    for (const pagina of new LegislativoPag().paginas()) {
      for (const acesso of acessosTotal) {
        if (acesso.pagina === Pagina[pagina]) {
          acessosSistema.push(acesso);
          break;
        }
      }
    }
    this.acessos.push({ sistema: 'legislativo', acessos: acessosSistema });

    // Patrimonio
    acessosSistema = [];
    for (const pagina of new PatrimonioPag().paginas()) {
      for (const acesso of acessosTotal) {
        if (acesso.pagina === Pagina[pagina]) {
          acessosSistema.push(acesso);
          break;
        }
      }
    }
    this.acessos.push({ sistema: 'patrimonio', acessos: acessosSistema });

    // Planejamento
    acessosSistema = [];
    for (const pagina of new PlanejamentoPag().paginas()) {
      for (const acesso of acessosTotal) {
        if (acesso.pagina === Pagina[pagina]) {
          acessosSistema.push(acesso);
          break;
        }
      }
    }
    this.acessos.push({ sistema: 'planejamento', acessos: acessosSistema });

    // PortalEntidade
    acessosSistema = [];
    for (const pagina of new PortalEntidadePag().paginas()) {
      for (const acesso of acessosTotal) {
        if (acesso.pagina === Pagina[pagina]) {
          acessosSistema.push(acesso);
          break;
        }
      }
    }
    this.acessos.push({ sistema: 'portal-entidade', acessos: acessosSistema });

    // Protocolo
    acessosSistema = [];
    for (const pagina of new ProtocoloPag().paginas()) {
      for (const acesso of acessosTotal) {
        if (acesso.pagina === Pagina[pagina]) {
          acessosSistema.push(acesso);
          break;
        }
      }
    }
    this.acessos.push({ sistema: 'protocolo', acessos: acessosSistema });

    // Folha
    acessosSistema = [];
    for (const pagina of new FolhaPag().paginas()) {
      for (const acesso of acessosTotal) {
        if (acesso.pagina === Pagina[pagina]) {
          acessosSistema.push(acesso);
          break;
        }
      }
    }
    this.acessos.push({ sistema: 'recursos-humanos', acessos: acessosSistema });

    // TerceiroSetor
    acessosSistema = [];
    for (const pagina of new TerceiroSetorPag().paginas()) {
      for (const acesso of acessosTotal) {
        if (acesso.pagina === Pagina[pagina]) {
          acessosSistema.push(acesso);
          break;
        }
      }
    }
    this.acessos.push({ sistema: 'terceiro-setor', acessos: acessosSistema });

    // Tesouraria
    acessosSistema = [];
    for (const pagina of new TesourariaPag().paginas()) {
      for (const acesso of acessosTotal) {
        if (acesso.pagina === Pagina[pagina]) {
          acessosSistema.push(acesso);
          break;
        }
      }
    }
    this.acessos.push({ sistema: 'tesouraria', acessos: acessosSistema });

    // Transparência
    acessosSistema = [];
    for (const pagina of new TransparenciaPag().paginas()) {
      for (const acesso of acessosTotal) {
        if (acesso.pagina === Pagina[pagina]) {
          acessosSistema.push(acesso);
          break;
        }
      }
    }
    this.acessos.push({ sistema: 'transparencia', acessos: acessosSistema });

    this.entidadeForm.get('acessos').setValue(this.acessos);
  }

  public adicionarUsuarioEstoque() {
    if (!this.estoqueAtual) return;

    if (!this.entidade.usuario_estoques) this.entidade.usuario_estoques = [];

    if (this.entidade.usuario_estoques.find(m => m.estoque.id === this.estoqueAtual.id)) {
      toastr.warning(`Estoque ${this.estoqueAtual.nome} já vinculado ao usuário!`);
    } else {
      this.entidade.usuario_estoques.push({ estoque: this.estoqueAtual });
    }
    this.estoqueAtual = undefined
  }

  public removerUsuarioEstoque(item: UsuarioEstoque, index: number) {
    if (!item) return

    this.entidade.usuario_estoques.splice(index, 1);
  }

  public adicionarUsuarioSetor() {
    if (!this.setorAtual) return;

    if (!this.entidade.usuario_setores) this.entidade.usuario_setores = [];

    if (this.entidade.usuario_setores.find(m => m.setor.id === this.setorAtual.id)) {
      toastr.warning(`Setor ${this.setorAtual.nome} já vinculado ao usuário!`);
    } else {
      this.entidade.usuario_setores.push({ setor: this.setorAtual });
    }
    this.setorAtual = undefined
  }

  public removerUsuarioSetor(item: UsuarioSetor, index: number) {
    if (!item) return

    this.entidade.usuario_setores.splice(index, 1);
  }
}
